/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
/**
 * @fileoverview Customer Company User logic module.
 * Handles customer company user operations including fetching users
 * by company, individual user retrieval, and active user queries.
 * @module logic/customerCompanyUser
 */

import { refreshToken, signOut } from "../../utils/common.js";

const LOG_RESPONSE_STATUS = "Response status:";
const LOG_RESPONSE_OK = "Response ok:";
const LOG_API_RESPONSE = "API Response data:";
const LOG_FETCH_USERS_ERROR = "Error fetching users:";

const BASE_CUSTOMER_COMPANY_USER_URL = `${process.env.REACT_APP_ROOT_API}customerCompanyUser/company/`;

/**
 * Fetches all customer company users for a specific company.
 * @async
 * @param {string} companyId - The company's unique identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of customer company user objects
 */
export const getCustomerCompanyUsersByCompanyId = async (
  companyId,
  navigate,
) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  logger.info("companyId INSIDE getCustomerCompanyUsersByCompanyId", {
    data: companyId,
  });

  try {
    logger.info("Fetching users for companyId:", { data: companyId });
    const response = await fetch(
      `${BASE_CUSTOMER_COMPANY_USER_URL}${companyId}`,
      options,
    );

    logger.info(LOG_RESPONSE_STATUS, { data: response.status });
    logger.info(LOG_RESPONSE_OK, { data: response.ok });

    const result = await response.json();
    logger.info(LOG_API_RESPONSE, { data: result });

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getCustomerCompanyUsersByCompanyId(companyId, navigate);
    }

    // Handle different response formats
    if (result.data) {
      logger.info("Returning result.data:", { data: result.data });
      return Array.isArray(result.data) ? result.data : [];
    } else if (Array.isArray(result)) {
      logger.info("Returning result array:", { data: result });
      return result;
    } else {
      logger.info("Returning empty array - result is not an array:", {
        data: result,
      });
      return [];
    }
  } catch (error) {
    logger.error(LOG_FETCH_USERS_ERROR, { err: sanitizeError(error) });
    return [];
  }
};

/**
 * Fetches all customer company users basic info for a specific company.
 * @async
 * @param {string} companyRFC - The company's RFC identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of customer company user objects
 */
export const getCustomerCompanyUsersBasicInfoByRFC = async (
  companyRFC,
  navigate,
) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    logger.info("Fetching users for companyRFC:", { data: companyRFC });
    const response = await fetch(
      `${BASE_CUSTOMER_COMPANY_USER_URL}basicInfo/${companyRFC}`,
      options,
    );

    logger.info(LOG_RESPONSE_STATUS, { data: response.status });
    logger.info(LOG_RESPONSE_OK, { data: response.ok });

    const result = await response.json();
    logger.info(LOG_API_RESPONSE, { data: result });

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getCustomerCompanyUsersBasicInfoByRFC(companyRFC, navigate);
    }

    if (!response.ok) {
      return [];
    }
    if (Array.isArray(result.data)) {
      return result.data;
    }
    if (Array.isArray(result)) {
      return result;
    }
    return [];
  } catch (error) {
    logger.error(LOG_FETCH_USERS_ERROR, { err: sanitizeError(error) });
    return [];
  }
};

/**
 * Fetches all customer company users basic info for a specific event.
 * @async
 * @param {string} eventId - The event's identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of customer company user objects
 */
export const getCustomerCompanyUsersBasicInfoByEventId = async (
  eventId,
  navigate,
) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    logger.info("Fetching users for eventId:", { data: eventId });
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}customerCompanyUser/event/${eventId}`,
      options,
    );

    logger.info(LOG_RESPONSE_STATUS, { data: response.status });
    logger.info(LOG_RESPONSE_OK, { data: response.ok });

    const result = await response.json();
    logger.info(LOG_API_RESPONSE, { data: result });

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getCustomerCompanyUsersBasicInfoByEventId(eventId, navigate);
    }

    if (!response.ok) {
      return [];
    }
    if (Array.isArray(result.data)) {
      return result.data;
    }
    if (Array.isArray(result)) {
      return result;
    }
    return [];
  } catch (error) {
    logger.error(LOG_FETCH_USERS_ERROR, { err: sanitizeError(error) });
    return [];
  }
};

/**
 * Fetches a single customer company user by ID.
 * @async
 * @param {string} customerCompanyUserId - User identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object|null>} User data or null on error
 */
export const getCustomerCompanyUser = async (
  customerCompanyUserId,
  navigate,
) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}customerCompanyUser/${customerCompanyUserId}`,
      options,
    );

    const result = await response.json();

    logger.info("getCustomerCompanyUser fetch result:", { data: result });

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await getCustomerCompanyUser(customerCompanyUserId, navigate);
    }

    return result.data;
  } catch (error) {
    logger.error("Error fetching customer company user:", {
      err: sanitizeError(error),
    });
    return null;
  }
};

/**
 * Fetches all active customer company users.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of active customer company user objects
 */
export const getActiveCustomerCompanyUsers = async (navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}customerCompanyUser/active`,
      options,
    );
    const result = await response.json();

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getActiveCustomerCompanyUsers(navigate);
    }
    if (!response.ok || result?.code === "FORBIDDEN") {
      return [];
    }
    return result.data || [];
  } catch (error) {
    logger.error("Error fetching active customer company users:", {
      err: sanitizeError(error),
    });
    return [];
  }
};

/**
 * Fetches all customer company users.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of all customer company user objects
 */
export const getCustomerCompanyUsers = async (navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}customerCompanyUser`,
      options,
    );
    const result = await response.json();
    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getCustomerCompanyUsers(navigate);
    }
    return result.data || [];
  } catch (error) {
    logger.error("Error fetching customer company users:", {
      err: sanitizeError(error),
    });
    return [];
  }
};
